/**
 * ProgressTracker Component
 *
 * Displays review session progress with statistics
 */

import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Speed,
} from '@mui/icons-material';

export interface ProgressTrackerProps {
  totalMessages: number;
  reviewedMessages: number;
  acceptedCount: number;
  rejectedCount: number;
  skippedCount?: number;
  sessionStartTime?: Date | string;
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  totalMessages,
  reviewedMessages,
  acceptedCount,
  rejectedCount,
  skippedCount = 0,
  sessionStartTime,
}) => {
  // Calculate progress percentage
  const progressPercentage = totalMessages > 0
    ? (reviewedMessages / totalMessages) * 100
    : 0;

  const remaining = totalMessages - reviewedMessages;

  // Calculate session duration
  const sessionDuration = sessionStartTime
    ? Math.floor((new Date().getTime() - new Date(sessionStartTime).getTime()) / 60000)
    : null;

  // Determine progress bar color based on percentage
  const getProgressColor = () => {
    if (progressPercentage >= 100) return 'success';
    if (progressPercentage >= 50) return 'primary';
    return 'secondary';
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Review Progress
        </Typography>

        {/* Statistics Chips */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
          <Chip
            icon={<CheckCircle />}
            label={`${acceptedCount} Accepted`}
            color="success"
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<Cancel />}
            label={`${rejectedCount} Rejected`}
            color="error"
            size="small"
            variant="outlined"
          />
          {skippedCount > 0 && (
            <Chip
              icon={<Speed />}
              label={`${skippedCount} Skipped`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            icon={<Schedule />}
            label={`${remaining} Remaining`}
            color="default"
            size="small"
            variant="outlined"
          />
        </Stack>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(progressPercentage, 100)}
            color={getProgressColor()}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
              },
            }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{
            minWidth: 65,
            fontWeight: 600,
            color: progressPercentage >= 100 ? 'success.main' : 'text.primary',
          }}
        >
          {progressPercentage.toFixed(1)}%
        </Typography>
      </Box>

      {/* Details */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {reviewedMessages.toLocaleString()} of {totalMessages.toLocaleString()} messages reviewed
        </Typography>

        {sessionDuration !== null && (
          <Typography variant="caption" color="text.secondary">
            Session time: {sessionDuration} {sessionDuration === 1 ? 'minute' : 'minutes'}
          </Typography>
        )}
      </Box>

      {/* Completion Message */}
      {progressPercentage >= 100 && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: 'success.light',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
            Session Complete! All messages have been reviewed.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ProgressTracker;
